import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type PatientUpdatedEventProps } from '@heidi/patients/contracts';

/**
 * The app-side emit host for `patient_updated` (`@heidi/patients`), openspec
 * task 2.1b.
 *
 * Ruling 24: the patient-profile drawer decides WHEN the event fires — after the
 * update mutation resolves, so a failed save does not record a review that never
 * happened — and assembles the payload, including the per-field
 * `extraction_fields_reviewed` outcome that only it can compute. This function
 * adds the `event_name` and the destination routing, and nothing else.
 *
 * ROUTING IS PRESERVED, NOT TIDIED: the call site passed
 * `options: { amplitude: false }` and so does this, because `patient_updated` is
 * a Snowplow event and the three OTHER emitters of this name (the two
 * `patientContext` write paths that go through
 * `dispatch.analyticsScribe.trackPatientUpdated`) must keep agreeing with it —
 * one name reaching two destination sets is a metric that disagrees with itself
 * depending on which surface produced the row.
 *
 * WHY THE ENUM MEMBER AND NOT `patientUpdated.name`. `event_name` discriminates
 * the app's `TrackingEventV2` union, whose members are typed by string ENUM
 * members, and TypeScript does not accept a string literal in an enum member's
 * place — so passing the descriptor's `.name` would not compile, and widening the
 * union's key to `string` would delete the event/props check. The nine
 * patient-SURFACE events escape this because their seam
 * (`PatientSurfaceAnalyticsEvent`) is narrowed to string literals; this one still
 * emits against the app union, so the enum member stays and the agreement is
 * ASSERTED in this module's suite instead, which is what makes the enum a
 * binding rather than a second spelling.
 */
export const trackPatientUpdated = (props: PatientUpdatedEventProps): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PatientUpdated,
    props,
  });
};
